import { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bell, Trash2, Settings, UserCheck, UserX, Ban, Loader2, Home, Check } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';
import { parseUTC } from '../utils/datetime';
import { beginPushPromptAction } from '../lib/pushPromptManager';
import {
  getDobRetryMessage,
  isDobRequiredError,
  PARTICIPATION_INTENT_TYPES,
  saveParticipationDobIntent,
} from '../lib/participationDob';

// Icon mapping for notification types
const NOTIFICATION_ICONS = {
  player_joined_your_run: '🏀',
  player_joined: '🏀',
  player_left_your_run: '➖',
  player_left: '➖',
  waitlist_added: '📋',
  waitlist_promoted: '✅',
  game_time_changed: '📅',
  game_location_changed: '📍',
  game_cancelled: '❌',
  game_reopened: '🔄',
  run_canceled: '❌',
  game_confirmed: '✅',
  game_updated: '📅',
  reminder: '⏰',
  admin_notice: '📢',
  comment_on_game: '💬',
  new_follow_request: '👤',
  follow_request_accepted: '✅',
  new_follow: '👤',
  game_invite: '✉️',  // New: Game invite notification
};

const Notifications = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { goBack } = useSafeBack('/dashboard');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'new' or 'all'
  const [unreadCount, setUnreadCount] = useState(0);
  const [smsOptIn, setSmsOptIn] = useState(null); // SMS opt-in status for banner
  const [actionLoading, setActionLoading] = useState({}); // Track loading state for individual actions

  useEffect(() => {
    if (!location.state?.dobCompletionSuccess) return;
    toast.success(getDobRetryMessage(location.state.participationIntentType));
    navigate('/notifications', { replace: true, state: null });
  }, [location.state, navigate]);

  const requestParticipationDob = () => {
    const saved = saveParticipationDobIntent({
      type: PARTICIPATION_INTENT_TYPES.INVITE_ACCEPT,
      returnTo: '/notifications',
    });
    if (!saved) {
      toast.error('Unable to open date of birth entry. Please try again.');
      return false;
    }
    navigate('/complete-dob');
    return true;
  };

  // Refetch notifications when page mounts or filter changes
  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchNotifications();
    fetchSmsStatus();
  }, [token, filter]);
  
  // Also refetch when component mounts (navigating back to this page)
  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, []); // Empty deps = only on mount

  // Fetch SMS opt-in status for banner display
  const fetchSmsStatus = async () => {
    try {
      const res = await apiClient.get('/user/notification-preferences');
      setSmsOptIn(res.data.sms_opt_in);
    } catch (err) {
      console.error('Failed to fetch SMS status:', err);
      console.error('Failed to fetch SMS status:', err);
      // Don't block UI if this fails
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/notifications?filter=${filter}`);
      
      // Handle both old and new API response formats — defensively normalize to an array.
      // The server returns { notifications: [...], unread_count: N } in the unified system,
      // but legacy/edge responses (null body, error shape, partially-deployed backend) must
      // not throw during render. Anything that isn't a proper array becomes [].
      const data = res.data ?? {};
      const rawList = Array.isArray(data) ? data : data.notifications;
      const notifList = Array.isArray(rawList) ? rawList : [];
      const unread = typeof data.unread_count === 'number'
        ? data.unread_count
        : notifList.filter(n => n && !n.is_read).length;
      
      setNotifications(notifList);
      setUnreadCount(unread);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      console.error('Failed to fetch notifications:', err);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already (optimistic update)
    if (!notification.is_read && notification.status !== 'READ') {
      // Update local state immediately for smooth UX.
      // On the New tab, drop the row from view so it doesn't flash during nav.
      // On the All tab, keep the row but flip its read state in place.
      setNotifications(prev =>
        filter === 'new'
          ? prev.filter(n => n.id !== notification.id)
          : prev.map(n =>
              n.id === notification.id ? { ...n, is_read: true, status: 'READ' } : n
            )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Fire off mark-as-read in background (don't await)
      apiClient.post('/notifications/bulk-mark-read', {
        notification_ids: [notification.id]
      }).catch(err => console.error('Failed to mark notification as read:', err));
    }
    
    // Determine navigation target
    const link = getNotificationLink(notification);
    
    if (link) {
      // Check if game exists before navigating (for game links)
      if (link.startsWith('/games/')) {
        const gameId = link.replace('/games/', '');
        try {
          const res = await apiClient.get(`/games/${gameId}`);
          if (res.data) {
            navigate(link);
            return;
          }
        } catch (err) {
          // Game doesn't exist or error - show message and go to dashboard
          if (err.response?.status === 404) {
            toast.error('This run is no longer available');
            navigate('/dashboard');
            return;
          }
          // For other errors, try navigating anyway
          navigate(link);
          return;
        }
      }
      
      // Handle legacy /profile/{user_id} links - redirect to /player/{username}
      if (link.startsWith('/profile/') && link !== '/profile') {
        // This is a legacy link format - try to get username from notification metadata
        const username = notification.metadata?.follower_name || 
                        notification.metadata?.accepter_name ||
                        notification.metadata?.requester_username;
        if (username) {
          navigate(`/player/${username}`);
          return;
        }
        // If no username available, show error
        toast.error('Unable to load this profile');
        return;
      }
      
      // Non-game links or successful game check
      navigate(link);
    } else {
      // Fallback - no link available
      toast.info('Unable to open this notification');
    }
  };
  
  /**
   * Get the deep link for a notification.
   * Priority: link field > game_id > entity_id > fallback
   */
  const getNotificationLink = (notification) => {
    // 1. Use explicit link field if present
    if (notification.link) {
      return notification.link;
    }
    
    // 2. Construct from game_id
    if (notification.game_id) {
      return `/games/${notification.game_id}`;
    }
    
    // 3. Construct from entity_id (for newer notification system)
    if (notification.entity_id) {
      // Assume entity_id is a game_id for game-related notifications
      const gameTypes = [
        'player_joined', 'player_joined_your_run',
        'player_left', 'player_left_your_run',
        'waitlist_added', 'waitlist_promoted',
        'game_cancelled', 'game_reopened', 'run_canceled',
        'game_confirmed', 'game_updated',
        'game_time_changed', 'game_location_changed',
        'comment_on_game', 'reminder'
      ];
      
      if (gameTypes.includes(notification.type)) {
        return `/games/${notification.entity_id}`;
      }
    }
    
    // 4. Try to get from payload
    if (notification.payload?.game_id) {
      return `/games/${notification.payload.game_id}`;
    }
    
    // 5. No link available
    return null;
  };

  /**
   * Mark a single notification as read WITHOUT navigating.
   *
   * - On the New tab: removes the row from view (server-side filter excludes
   *   read notifications, so this matches what the next fetch would return).
   * - On the All tab: flips the row to read in place so the visual hierarchy
   *   updates immediately.
   * - Decrements the unread count optimistically.
   * - Uses the existing idempotent `bulk-mark-read` endpoint so no rollback
   *   is needed on transient errors.
   */
  const handleMarkRead = async (e, notification) => {
    e.stopPropagation(); // Prevent row navigation

    // No-op if already read (defensive — button is hidden in that state).
    if (notification.is_read || notification.status === 'READ') return;

    if (filter === 'new') {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    } else {
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id ? { ...n, is_read: true, status: 'READ' } : n
        )
      );
    }
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await apiClient.post('/notifications/bulk-mark-read', {
        notification_ids: [notification.id]
      });
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Idempotent endpoint — silent failure is acceptable. The next fetch
      // will reconcile if the server actually didn't process the update.
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation(); // Prevent navigation
    
    try {
      await apiClient.post('/notifications/delete', {
        notification_id: notificationId
      });
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (err) {
      console.error('Failed to delete notification:', err);
      console.error('Failed to delete notification:', err);
      toast.error('Failed to delete notification');
    }
  };

  // ==================== Follow Request Action Handlers ====================
  
  const handleAcceptFollowRequest = async (e, notification) => {
    e.stopPropagation(); // Prevent navigation
    
    const requestId = notification.metadata?.request_id;
    if (!requestId) {
      toast.error('Invalid follow request');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [`accept-${notification.id}`]: true }));
    
    try {
      await apiClient.post(`/follow/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark as read and update message (keeps in All, removes from New)
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Accepted)` }
          : n
      ));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // If on "new" filter, remove from view
      if (filter === 'new') {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }
      
      toast.success(`Accepted follow request from ${notification.metadata?.requester_username || 'user'}`);
    } catch (err) {
      console.error('Failed to accept follow request:', err);
      console.error('Failed to accept follow request:', err);
      if (err.response?.status === 404) {
        toast.error('Follow request no longer exists');
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Expired)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else {
        toast.error('Failed to accept follow request');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [`accept-${notification.id}`]: false }));
    }
  };
  
  const handleDeclineFollowRequest = async (e, notification) => {
    e.stopPropagation(); // Prevent navigation
    
    const requestId = notification.metadata?.request_id;
    if (!requestId) {
      toast.error('Invalid follow request');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [`decline-${notification.id}`]: true }));
    
    try {
      await apiClient.post(`/follow/requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark as read and update message (keeps in All, removes from New)
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Declined)` }
          : n
      ));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // If on "new" filter, remove from view
      if (filter === 'new') {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }
      
      toast.success('Follow request declined');
    } catch (err) {
      console.error('Failed to decline follow request:', err);
      console.error('Failed to decline follow request:', err);
      if (err.response?.status === 404) {
        toast.error('Follow request no longer exists');
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Expired)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else {
        toast.error('Failed to decline follow request');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [`decline-${notification.id}`]: false }));
    }
  };
  
  const handleBlockFromFollowRequest = async (e, notification) => {
    e.stopPropagation(); // Prevent navigation
    
    const requesterId = notification.metadata?.requester_id;
    if (!requesterId) {
      toast.error('Cannot block this user');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [`block-${notification.id}`]: true }));
    
    try {
      await apiClient.post(`/block/${requesterId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove notification from list
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      toast.success(`Blocked ${notification.metadata?.requester_username || 'user'}`);
    } catch (err) {
      console.error('Failed to block user:', err);
      console.error('Failed to block user:', err);
      toast.error('Failed to block user');
    } finally {
      setActionLoading(prev => ({ ...prev, [`block-${notification.id}`]: false }));
    }
  };
  
  // Check if this is a follow request notification
  const isFollowRequestNotification = (notification) => {
    return notification.type === 'new_follow_request';
  };

  // ==================== Game Invite Action Handlers ====================
  
  const isGameInviteNotification = (notification) => {
    // Accept the canonical "game_invite" type plus two legacy types that still
    // exist in production data:
    //   - "game_invite_sent" — fallback enum from older orchestrator code paths
    //     (49 rows in prod, plus any inflight before this deploy)
    //   - "squad_invite"     — direct-write path for quick-invite drawer
    //     (21 rows in prod, now migrated to "game_invite" going forward but
    //      legacy rows must still render correctly)
    return ['game_invite', 'game_invite_sent', 'squad_invite'].includes(notification.type);
  };

  const handleAcceptInvite = async (e, notification) => {
    e.stopPropagation();

    if (!user?.date_of_birth) {
      requestParticipationDob();
      return;
    }
    
    const inviteId = notification.metadata?.invite_id;
    if (!inviteId) {
      toast.error('Invalid invite');
      return;
    }
    
    const finishPromptAction = beginPushPromptAction();
    setActionLoading(prev => ({ ...prev, [`accept-invite-${notification.id}`]: true }));
    
    try {
      const response = await apiClient.post(`/invites/${inviteId}/respond`, { action: 'accept' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark notification as read on the server using the supported bulk endpoint.
      // (Prior call to `/notifications/${id}/read` was a 404 — that route does not
      // exist; bell badge drifted back up after the next 30s poll.)
      await apiClient.post('/notifications/bulk-mark-read', {
        notification_ids: [notification.id],
      }).catch(() => {}); // Ignore errors
      
      // Update notification in local state to mark as read (keeps it in All, removes from New)
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Joined)` }
          : n
      ));
      
      // If on "new" filter, remove from view (but it stays in All)
      if (filter === 'new') {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      if (response.data.already_joined) {
        toast.success('You\'re already in this game!');
      } else {
        toast.success('Joined the game!');
      }
      
      // Push permission ask after successful invite-accept (Capacitor-only no-op).
      try { finishPromptAction.accepted(); } catch { /* noop */ }
      
      // Navigate to the game
      const gameId = notification.game_id || notification.metadata?.game_id;
      if (gameId) {
        navigate(`/games/${gameId}`);
      }
    } catch (err) {
      if (isDobRequiredError(err)) {
        requestParticipationDob();
        return;
      }
      console.error('Failed to accept invite:', err);
      console.error('Failed to accept invite:', err);
      const detail = err.response?.data?.detail || '';
      
      if (err.response?.status === 404) {
        toast.error('Invite no longer exists');
        // Mark as read but keep in history with note
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Expired)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else if (detail.includes('already declined')) {
        toast.error('This invite was already declined');
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Declined)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else if (detail.includes('already accepted')) {
        toast.info('Invite already accepted');
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Joined)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
        const gameId = notification.game_id || notification.metadata?.game_id;
        if (gameId) {
          navigate(`/games/${gameId}`);
        }
      } else {
        toast.error(detail || 'Failed to accept invite');
      }
    } finally {
      finishPromptAction();
      setActionLoading(prev => ({ ...prev, [`accept-invite-${notification.id}`]: false }));
    }
  };

  const handleDeclineInvite = async (e, notification) => {
    e.stopPropagation();
    
    const inviteId = notification.metadata?.invite_id;
    if (!inviteId) {
      toast.error('Invalid invite');
      return;
    }
    
    const finishPromptAction = beginPushPromptAction();
    setActionLoading(prev => ({ ...prev, [`decline-invite-${notification.id}`]: true }));
    
    try {
      await apiClient.post(`/invites/${inviteId}/respond`, { action: 'decline' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark notification as read on the server using the supported bulk endpoint.
      // (Prior call to `/notifications/${id}/read` was a 404 — that route does not
      // exist; bell badge drifted back up after the next 30s poll.)
      await apiClient.post('/notifications/bulk-mark-read', {
        notification_ids: [notification.id],
      }).catch(() => {}); // Ignore errors if notification marking fails
      
      // Update THIS SPECIFIC notification in local state to mark as read
      // Use notification.id to ensure we only update this exact notification
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Declined)` }
          : n
      ));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // If on "new" filter, remove this specific notification from view (but it stays in All)
      if (filter === 'new') {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }
      
      toast.success('Invite declined');
      // No navigation - stay on notifications page
    } catch (err) {
      console.error('Failed to decline invite:', err);
      console.error('Failed to decline invite:', err);
      const detail = err.response?.data?.detail || '';
      
      if (err.response?.status === 404) {
        toast.error('Invite no longer exists');
        // Mark as read but keep in history
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ', message: `${n.message} (Expired)` }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else if (detail.includes('already')) {
        // Invite was already responded to - update this notification
        toast.info('Invite already processed');
        setNotifications(prev => prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, status: 'READ' }
            : n
        ));
        if (filter === 'new') {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } else {
        toast.error(detail || 'Failed to decline invite');
      }
    } finally {
      finishPromptAction();
      setActionLoading(prev => ({ ...prev, [`decline-invite-${notification.id}`]: false }));
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post('/notifications/mark-all-read');
      // Match individual Mark Read semantics: on New tab, clear the list
      // (server filter excludes read items). On All tab, flip in place.
      setNotifications(prev =>
        filter === 'new'
          ? []
          : prev.map(n => ({ ...n, is_read: true, status: 'READ' }))
      );
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      console.error('Failed to mark all as read:', err);
      toast.error('Failed to mark all as read');
    }
  };

  const handleFilterChange = (newFilter) => {
    if (newFilter !== filter) {
      setFilter(newFilter);
    }
  };

  // Group notifications by date.
  // Defensive: notifications with missing/invalid `created_at` would otherwise
  // crash the render tree (parseUTC -> null -> .getFullYear()), unmounting the
  // app shell and producing the blank-screen bug on iOS. Skip those silently.
  const groupByDate = (notifications) => {
    const groups = {};
    if (!Array.isArray(notifications)) return groups;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach(notification => {
      if (!notification) return;
      const createdAt = parseUTC(notification.created_at);
      if (!createdAt || Number.isNaN(createdAt.getTime())) {
        // Bad timestamp — bucket as 'Earlier' instead of dropping so the user still sees the row.
        if (!groups['Earlier']) groups['Earlier'] = [];
        groups['Earlier'].push(notification);
        return;
      }
      const notificationDate = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
      
      let groupKey;
      if (notificationDate.getTime() === today.getTime()) {
        groupKey = 'Today';
      } else if (notificationDate.getTime() === yesterday.getTime()) {
        groupKey = 'Yesterday';
      } else {
        groupKey = 'Earlier';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(notification);
    });

    return groups;
  };

  // Format relative time — defensive against null/invalid timestamps.
  const formatRelativeTime = (dateStr) => {
    const date = parseUTC(dateStr);
    if (!date || Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const groupedNotifications = groupByDate(notifications);
  const displayUnreadCount = filter === 'new' ? notifications.length : unreadCount;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-[#FFFFFF] border-b border-[#E4E4E7]"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto max-w-2xl px-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={goBack}
                className="h-10 w-10 rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="h-8 w-8 rounded-full"
                data-testid="nav-home-btn"
                aria-label="Dashboard"
              >
                <Home className="w-4 h-4 text-zinc-400" />
              </Button>
              <h1 className="text-xl font-bold text-zinc-900 ml-1">Notifications</h1>
              {displayUnreadCount > 0 && (
                <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                  {displayUnreadCount} new
                </span>
              )}
            </div>
            {/* Settings Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/notification-settings')}
              className="h-10 w-10 rounded-full"
              data-testid="notification-settings-btn"
            >
              <Settings className="w-5 h-5 text-zinc-500" />
            </Button>
          </div>
          
          {/* Filter Toggle */}
          <div className="flex items-center gap-2 mt-4">
            <div className="flex bg-zinc-100 rounded-lg p-1">
              <button
                onClick={() => handleFilterChange('new')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === 'new' 
                    ? 'bg-white text-zinc-900 shadow-sm' 
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
                data-testid="filter-new-btn"
              >
                New
              </button>
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === 'all' 
                    ? 'bg-white text-zinc-900 shadow-sm' 
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
                data-testid="filter-all-btn"
              >
                All
              </button>
            </div>
            
            {unreadCount > 0 && filter === 'all' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-sm text-zinc-600 hover:text-zinc-900 ml-auto"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* SMS Opt-in Banner - Show when sms_opt_in is false */}
      {smsOptIn === false && (
        <div className="mx-auto max-w-2xl px-4 pt-4">
          <div 
            className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-4"
            data-testid="sms-opt-in-banner"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Bell className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-medium text-zinc-900">Turn on SMS alerts</p>
                <p className="text-sm text-zinc-600">Get game updates and reminders via text</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/notification-settings')}
              className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
              size="sm"
              data-testid="enable-sms-banner-btn"
            >
              Enable SMS
            </Button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-2xl px-4 py-4">
        {loading ? (
          <div className="text-center py-16">
            <p className="text-zinc-500">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 text-lg mb-2">
              {filter === 'new' ? 'No new notifications' : 'No notifications yet'}
            </p>
            <p className="text-zinc-500 text-sm">
              {filter === 'new' 
                ? 'You\'re all caught up!'
                : 'When someone joins your run or you get updates, they\'ll appear here.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {['Today', 'Yesterday', 'Earlier'].map(group => {
              const items = groupedNotifications[group];
              if (!items || items.length === 0) return null;

              return (
                <div key={group}>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 px-1">
                    {group}
                  </h2>
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm divide-y divide-zinc-100">
                    {items.map(notification => {
                      const isUnread = !notification.is_read && notification.status !== 'READ';
                      const hasLink = !!(notification.link || notification.game_id || notification.entity_id || notification.payload?.game_id);
                      const isFollowRequest = isFollowRequestNotification(notification);
                      const isGameInvite = isGameInviteNotification(notification);
                      const hasInlineActions = isFollowRequest || isGameInvite;
                      
                      return (
                        <div
                          key={notification.id}
                          onClick={() => !hasInlineActions && handleNotificationClick(notification)}
                          className={`w-full text-left px-4 py-4 transition-colors flex items-start gap-3 group ${
                            isUnread ? 'bg-orange-50/50' : ''
                          } ${hasLink && !hasInlineActions ? 'hover:bg-zinc-50 cursor-pointer' : 'cursor-default'}`}
                          data-testid={`notification-${notification.id}`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && !hasInlineActions && handleNotificationClick(notification)}
                        >
                          <span className="text-xl flex-shrink-0">
                            {NOTIFICATION_ICONS[notification.type] || '🔔'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${isUnread ? 'font-semibold text-zinc-900' : 'text-zinc-700'}`}>
                              {notification.message}
                            </p>
                            {(notification.metadata?.game_title || notification.payload?.game_title) && (
                              <p className="text-sm text-zinc-700 mt-0.5 truncate">
                                {notification.metadata?.game_title || notification.payload?.game_title}
                              </p>
                            )}
                            <p className="text-[13px] text-zinc-500 mt-1">
                              {formatRelativeTime(notification.created_at)}
                            </p>
                            
                            {/* Follow Request Actions - Inline */}
                            {isFollowRequest && (
                              <div className="flex items-center gap-2 mt-3" data-testid={`follow-request-actions-${notification.id}`}>
                                <Button
                                  size="sm"
                                  onClick={(e) => handleAcceptFollowRequest(e, notification)}
                                  disabled={actionLoading[`accept-${notification.id}`] || actionLoading[`decline-${notification.id}`] || actionLoading[`block-${notification.id}`]}
                                  className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-3"
                                  data-testid={`accept-follow-${notification.id}`}
                                >
                                  {actionLoading[`accept-${notification.id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <UserCheck className="w-3 h-3 mr-1" />
                                      Accept
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => handleDeclineFollowRequest(e, notification)}
                                  disabled={actionLoading[`accept-${notification.id}`] || actionLoading[`decline-${notification.id}`] || actionLoading[`block-${notification.id}`]}
                                  className="h-8 px-3"
                                  data-testid={`decline-follow-${notification.id}`}
                                >
                                  {actionLoading[`decline-${notification.id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <UserX className="w-3 h-3 mr-1" />
                                      Decline
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => handleBlockFromFollowRequest(e, notification)}
                                  disabled={actionLoading[`accept-${notification.id}`] || actionLoading[`decline-${notification.id}`] || actionLoading[`block-${notification.id}`]}
                                  className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`block-user-${notification.id}`}
                                >
                                  {actionLoading[`block-${notification.id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Ban className="w-3 h-3 mr-1" />
                                      Block
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            
                            {/* Game Invite Actions - Inline */}
                            {isGameInvite && (
                              <div className="flex items-center gap-2 mt-3" data-testid={`game-invite-actions-${notification.id}`}>
                                <Button
                                  size="sm"
                                  onClick={(e) => handleAcceptInvite(e, notification)}
                                  disabled={actionLoading[`accept-invite-${notification.id}`] || actionLoading[`decline-invite-${notification.id}`]}
                                  className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-3"
                                  data-testid={`accept-invite-${notification.id}`}
                                >
                                  {actionLoading[`accept-invite-${notification.id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    'Join'
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => handleDeclineInvite(e, notification)}
                                  disabled={actionLoading[`accept-invite-${notification.id}`] || actionLoading[`decline-invite-${notification.id}`]}
                                  className="h-8 px-3"
                                  data-testid={`decline-invite-${notification.id}`}
                                >
                                  {actionLoading[`decline-invite-${notification.id}`] ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    'Decline'
                                  )}
                                </Button>
                                {/* View Game link */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const gameId = notification.game_id || notification.metadata?.game_id;
                                    if (gameId) navigate(`/games/${gameId}`);
                                  }}
                                  className="text-xs text-zinc-500 hover:text-orange-500 underline ml-2"
                                  data-testid={`view-game-${notification.id}`}
                                >
                                  View Game
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isUnread && (
                              <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                            )}
                            {hasLink && !hasInlineActions && (
                              <span className="text-zinc-300 group-hover:text-zinc-500 transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </span>
                            )}
                            {isUnread && !hasInlineActions && (
                              <button
                                onClick={(e) => handleMarkRead(e, notification)}
                                aria-label="Mark as read"
                                title="Mark as read"
                                className="p-1.5 rounded-full text-zinc-400 hover:text-orange-600 hover:bg-orange-50 transition-all"
                                data-testid={`mark-read-${notification.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteNotification(e, notification.id)}
                              className="p-1.5 rounded-full text-zinc-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                              data-testid={`delete-notification-${notification.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer variant="slim" />
    </div>
  );
};

export default Notifications;
